import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { FC } from 'react'
import { useNavigate } from 'react-router'
import { WQISignal } from '../../../store/waterQualityIndex'

interface WQIParameterDrawerProps {
  open: boolean
  onClose: () => void
  signal: WQISignal | null
}

export const WQIParameterDrawer: FC<WQIParameterDrawerProps> = ({
  open,
  onClose,
  signal,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()

  if (!signal) return null

  const isUnsafe = signal.severity === 'action'
  const isMonitor = signal.severity === 'monitor'

  // Determine status config
  const getStatusConfig = () => {
    if (isUnsafe) {
      return {
        label: 'Take action',
        message: 'This signal requires immediate attention',
        backgroundColor: '#FEF5F6',
        color: '#621B16',
        iconColor: '#ED1112',
      }
    }
    if (isMonitor) {
      return {
        label: 'Monitor',
        message: 'This signal requires monitoring',
        backgroundColor: '#FFFBEB',
        color: '#92400E',
        iconColor: '#F59E0B',
      }
    }
    return {
      label: 'Normal',
      message: 'This signal is within safe levels',
      backgroundColor: '#E6F8EF',
      color: '#065F46',
      iconColor: '#10B981',
    }
  }

  const statusConfig = getStatusConfig()

  // Format signal key to display name
  const formatSignalName = (signalKey: string) => {
    return signalKey
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560 },
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        },
      }}
    >
      {/* Header */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {signal.statement || formatSignalName(signal.signalKey)}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ color: theme.palette.grey[500] }} />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Status Alert Box */}
        <Box
          sx={{
            backgroundColor: statusConfig.backgroundColor,
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            {isUnsafe || isMonitor ? (
              <WarningAmberIcon sx={{ color: statusConfig.iconColor, fontSize: 24 }} />
            ) : (
              <CheckCircleOutlineIcon sx={{ color: statusConfig.iconColor, fontSize: 24 }} />
            )}
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: statusConfig.color }}>
                {statusConfig.label}
              </Typography>
              <Typography variant="body2" sx={{ color: statusConfig.color }}>
                {statusConfig.message}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Why it matters */}
        <Box sx={{ mb: 3, pb: 2, borderBottom: `1px solid ${theme.palette.grey[200]}` }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            Why it matters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {signal.whyItMatters}
          </Typography>
        </Box>

        {/* Mitigation Steps */}
        {(signal.mitigation.immediateSteps.length > 0 ||
          signal.mitigation.longTermSteps.length > 0) && (
          <Box sx={{ mb: 3, pb: 2, borderBottom: `1px solid ${theme.palette.grey[200]}` }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Recommended Actions
            </Typography>
            {signal.mitigation.immediateSteps.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Immediate steps:
                </Typography>
                <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                  {signal.mitigation.immediateSteps.map((step, idx) => (
                    <Typography key={idx} component="li" variant="body2" color="text.secondary">
                      {step}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
            {signal.mitigation.longTermSteps.length > 0 && (
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Long-term steps:
                </Typography>
                <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                  {signal.mitigation.longTermSteps.map((step, idx) => (
                    <Typography key={idx} component="li" variant="body2" color="text.secondary">
                      {step}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* Evidence Section */}
        {signal.evidence.length > 0 && (
          <Box
            sx={{
              backgroundColor: '#F3F6F8',
              borderRadius: 2,
              p: 2,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <SearchIcon sx={{ color: theme.palette.text.primary }} />
              <Typography variant="h6" fontWeight={600}>
                Evidence
              </Typography>
            </Stack>

            <Stack spacing={1}>
              {signal.evidence.map((evidence, idx) => (
                <Box
                  key={idx}
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Source:
                    </Typography>
                    <Chip
                      label={evidence.source === 'test_strip' ? 'Test strip' : evidence.source}
                      size="small"
                      sx={{
                        backgroundColor:
                          evidence.source === 'test_strip' ? '#FFEDD5' : '#E6EEF4',
                        color: evidence.source === 'test_strip' ? '#9A3412' : '#003E63',
                        fontSize: '12px',
                        height: '22px',
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {evidence.detail}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Related Contaminants */}
        {signal.relatedEpaContaminants.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Related EPA Contaminants:
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {signal.relatedEpaContaminants.map((contaminant, idx) => (
                <Chip
                  key={idx}
                  label={contaminant}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Affected Attributes */}
        {signal.affectedAttributes.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Affected Parameters:
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {signal.affectedAttributes.map((attr, idx) => (
                <Chip
                  key={idx}
                  label={attr.replace(/_/g, ' ')}
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: 'capitalize' }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Bottom Button */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        sx={{
          mt: 3,
          py: 2,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '16px',
        }}
        onClick={() => {
          onClose()
          navigate('/profile/recommendations')
        }}
      >
        View your treatment plan
      </Button>
    </Drawer>
  )
}
